import { useUser } from '@ad-lucy/gateway-provider';
import { useTheme } from '@ad-lucy/ui';
import { useOrganizationBrandAppearanceQuery } from '@codegen';
import { useI18n } from '@i18n/i18n.utils';
import { getAppConfig } from '@modules/config/appConfig';
import styled from 'styled-components';
import { HomeLink, LinkIcon } from './Navbar/components';

const Container = styled.div`
  padding-top: ${(props) => Number.parseInt(props.theme?.spacing?.xxs, 10) * 7}px;
  padding-bottom: ${(props) => Number.parseInt(props.theme?.spacing?.xxs, 10) * 5}px;
`;

type StyledImageContainerProps = {
  visible?: boolean;
  collapsed?: boolean;
};

const StyledImageContainer = styled.div<StyledImageContainerProps>`
  width: ${({ visible, collapsed }) => {
    if (!visible) {
      return 0;
    }
    return collapsed ? '32px' : '100%';
  }};
  height: 32px;
  background-position: ${({ collapsed }) => (collapsed ? 'center' : 'left')};
  background-repeat: no-repeat;
  background-size: ${({ collapsed }) => (collapsed ? 'contain' : 'auto 100%')};
`;

type StyledLinkIconProps = {
  visible?: boolean;
};

const StyledLinkIcon = styled(LinkIcon)<StyledLinkIconProps>`
  width: ${({ visible }) => !visible && '0'};
  align-self: center;
  justify-self: center;
`;

const squareLogoSrcLocalStorageKey = 'adm.sidebar.header.squareLogoSrc';

function getLogoSrc(uuid: string | undefined) {
  return `${getAppConfig().IMG_URL}${uuid ?? ''}`;
}

type HeaderProps = Readonly<{
  collapsed?: boolean;
}>;
function Header({ collapsed = false }: HeaderProps) {
  const { t } = useI18n();
  const { theme } = useTheme();
  const appConfig = getAppConfig();
  const user = useUser();

  const organizationBrandAppearanceQuery = useOrganizationBrandAppearanceQuery({
    onCompleted: (data) => {
      if (data?.organizationBrandAppearance?.squareLogo?.uuid) {
        localStorage.setItem(
          squareLogoSrcLocalStorageKey,
          getLogoSrc(
            organizationBrandAppearanceQuery.data?.organizationBrandAppearance
              ?.squareLogo?.uuid,
          ),
        );
      } else {
        localStorage.removeItem(squareLogoSrcLocalStorageKey);
      }
    },
  });

  let squareLogoSrc = '';
  // Use cached logo src to mount the sidebar on app start already displaying the logo
  if (organizationBrandAppearanceQuery.loading) {
    squareLogoSrc = localStorage?.getItem(squareLogoSrcLocalStorageKey) ?? '';
  }
  if (
    organizationBrandAppearanceQuery.data?.organizationBrandAppearance
      ?.squareLogo?.uuid
  ) {
    squareLogoSrc = getLogoSrc(
      organizationBrandAppearanceQuery.data?.organizationBrandAppearance
        ?.squareLogo?.uuid,
    );
  } else if (!squareLogoSrc && theme?.logos?.small) {
    // https://factor-eleven.atlassian.net/browse/FOUN-948: this condition can be removed
    // it's left for backwards compatibility
    squareLogoSrc = `${appConfig.THEME_URL}logos/${theme?.logos?.small}`;
  }

  let landscapeLogoSrc = '';
  if (
    organizationBrandAppearanceQuery.data?.organizationBrandAppearance
      ?.landscapeLogo?.uuid
  ) {
    landscapeLogoSrc = getLogoSrc(
      organizationBrandAppearanceQuery.data?.organizationBrandAppearance
        ?.landscapeLogo?.uuid,
    );
  } else if (!landscapeLogoSrc && theme?.logos?.default) {
    // https://factor-eleven.atlassian.net/browse/FOUN-948: this condition can be removed
    // it's left for backwards compatibility
    landscapeLogoSrc = `${appConfig.THEME_URL}logos/${theme?.logos?.default}`;
  }

  // Mount both logo types in the DOM to avoid triggering the refetch of the logo.
  return (
    <Container>
      <HomeLink
        to={user.require('quickstart:view') ? '/quickstart' : '/campaigns'}
        title={t('global.homepage')}
        $collapsed={collapsed}
        $active={false}
        text={t('global.homepage')}
      >
        <StyledImageContainer
          visible={!collapsed}
          collapsed={collapsed}
          style={{ backgroundImage: `url(${landscapeLogoSrc})` }}
          data-testid="adm.sidebar.logo"
        />
        {squareLogoSrc ? (
          <StyledImageContainer
            visible={collapsed}
            collapsed={collapsed}
            style={{ backgroundImage: `url(${squareLogoSrc})` }}
          />
        ) : (
          <StyledLinkIcon visible={collapsed} type="IconHomeLine" size="md" />
        )}
      </HomeLink>
    </Container>
  );
}

export default Header;
